import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { domainsApi, policyAdminApi } from '../api';
import type {
  PolicyAgentChatMessage,
  PolicyAgentChatResponse,
  PolicyChange,
  PolicyDraftEvent,
  PolicyImpactReport,
  PolicyRuleCounts,
  ReviewPolicyDoc,
  ReviewPolicyFinding,
  ReviewPolicyOverview,
  ReviewPolicySeverity,
} from '../types';

const SEV_COLORS: Record<ReviewPolicySeverity, { bg: string; text: string; dot: string; border: string }> = {
  BLOCKER: { bg: '#fef2f2', text: '#b91c1c', dot: '#dc2626', border: '#fecaca' },
  FLAG: { bg: '#fffbeb', text: '#b45309', dot: '#d97706', border: '#fde68a' },
  ADVISORY: { bg: '#f9fafb', text: '#6b7280', dot: '#9ca3af', border: '#e5e7eb' },
};

const SEV_LABEL: Record<ReviewPolicySeverity, string> = {
  BLOCKER: 'Blocks sign-off',
  FLAG: 'Needs review',
  ADVISORY: 'Info only',
};

const GROUP_NAMES: Record<string, string> = {
  PID: 'P&ID Sheet',
  NODE: 'Lines',
  EQ: 'Equipment',
  INST: 'Instruments',
  ILK: 'Interlocks',
  LOOP: 'Control Loops',
  REL: 'Relationships',
  XDOC: 'Cross-checks',
};

const TABS = ['Policy Rules', 'Instrument Requirements', 'Agent', 'Fleet Impact', 'Versions & Compare'] as const;
type Tab = (typeof TABS)[number];

const btnStyle: React.CSSProperties = {
  borderRadius: 7,
  border: '1px solid rgba(0,0,0,0.1)',
  backgroundColor: 'white',
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: '#334155',
  cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle,
  backgroundColor: '#2563eb',
  borderColor: '#2563eb',
  color: 'white',
};

function clonePolicy(policy: ReviewPolicyDoc): ReviewPolicyDoc {
  return JSON.parse(JSON.stringify(policy));
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return new Date(iso).toLocaleString();
}

const SevChip: React.FC<{ sev?: string }> = ({ sev }) => {
  const c = SEV_COLORS[(sev as ReviewPolicySeverity) ?? 'ADVISORY'] ?? SEV_COLORS.ADVISORY;
  if (!sev) return <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 999,
        border: `1px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.text,
        padding: '1px 8px',
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.dot }} />
      {sev} · {SEV_LABEL[sev as ReviewPolicySeverity] ?? ''}
    </span>
  );
};

/** Renders a diff in the SAME format the policy itself is shown: rule cards
 * with before → after values, and instrument-requirement rows. */
const PolicyDiff: React.FC<{
  changes: PolicyChange[];
  afterPolicy?: ReviewPolicyDoc | null;
  beforePolicy?: ReviewPolicyDoc | null;
}> = ({ changes, afterPolicy, beforePolicy }) => {
  if (!changes.length) {
    return <div style={{ padding: 12, fontSize: 12, color: '#9ca3af' }}>The two versions are identical.</div>;
  }
  const ruleChanges = new Map<string, PolicyChange[]>();
  const matrixChanges: PolicyChange[] = [];
  const otherChanges: PolicyChange[] = [];
  for (const c of changes) {
    if (c.kind.startsWith('rule')) {
      ruleChanges.set(c.rule!, [...(ruleChanges.get(c.rule!) ?? []), c]);
    } else if (c.kind.startsWith('matrix')) {
      matrixChanges.push(c);
    } else {
      otherChanges.push(c);
    }
  }
  const fieldLabel: Record<string, string> = {
    severity: 'Severity',
    enabled: 'Active',
    title: 'Rule name',
    description: 'Description',
    fires_when: 'Fires when',
    example: 'Example',
    signoff_only: 'Only at sign-off',
  };
  const renderValue = (field: string | undefined, value: unknown) => {
    if (field === 'severity') return <SevChip sev={value as string} />;
    if (field === 'enabled' || field === 'signoff_only')
      return <span style={{ fontWeight: 700, color: value ? '#059669' : '#b91c1c' }}>{value ? 'yes' : 'no'}</span>;
    return <span style={{ color: '#374151' }}>{String(value ?? '—')}</span>;
  };
  return (
    <div>
      {Array.from(ruleChanges.entries()).map(([ruleId, ruleCs]) => {
        const added = ruleCs.some((c) => c.kind === 'rule_added');
        const removed = ruleCs.some((c) => c.kind === 'rule_removed');
        const cfg =
          (afterPolicy?.rules?.[ruleId] as Record<string, unknown> | undefined) ??
          (beforePolicy?.rules?.[ruleId] as Record<string, unknown> | undefined) ??
          (ruleCs[0]?.after as Record<string, unknown> | undefined) ??
          (ruleCs[0]?.before as Record<string, unknown> | undefined);
        return (
          <div
            key={ruleId}
            style={{
              border: `1px solid ${removed ? '#fecaca' : added ? '#a7f3d0' : '#fde68a'}`,
              borderRadius: 10,
              marginBottom: 8,
              backgroundColor: 'white',
              overflow: 'hidden',
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 12px',
                backgroundColor: removed ? '#fef2f2' : added ? '#ecfdf3' : '#fffbeb',
              }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: removed ? '#b91c1c' : added ? '#047857' : '#92400e' }}>
                {removed ? 'REMOVED' : added ? 'NEW RULE' : 'CHANGED'}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{(cfg?.title as string) ?? ruleId}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af' }}>{ruleId}</span>
            </div>
            {added && cfg && (
              <div style={{ padding: '7px 12px', display: 'flex', gap: 12, alignItems: 'center', fontSize: 12 }}>
                <SevChip sev={cfg.severity as string} />
                <span style={{ color: '#6b7280' }}>{(cfg.description as string) ?? ''}</span>
              </div>
            )}
            {!added && !removed &&
              ruleCs.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 12px', borderTop: '1px solid #f8fafc', fontSize: 12 }}>
                  <span style={{ width: 110, fontWeight: 700, color: '#6b7280', fontSize: 11 }}>{fieldLabel[c.field ?? ''] ?? c.field}</span>
                  {renderValue(c.field, c.before)}
                  <span style={{ color: '#9ca3af' }}>→</span>
                  {renderValue(c.field, c.after)}
                </div>
              ))}
          </div>
        );
      })}
      {matrixChanges.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
          <div style={{ padding: '6px 12px', backgroundColor: '#f9fafb', fontSize: 11, fontWeight: 800, color: '#374151' }}>
            INSTRUMENT REQUIREMENTS
          </div>
          {matrixChanges.map((c, i) => {
            const entry = (c.after ?? c.before) as { rule?: string; message?: string } | undefined;
            const removed = c.kind === 'matrix_removed';
            const added = c.kind === 'matrix_added';
            return (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 12px', borderTop: '1px solid #f8fafc', fontSize: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: removed ? '#b91c1c' : added ? '#047857' : '#92400e', width: 66 }}>
                  {removed ? 'REMOVED' : added ? 'NEW' : 'CHANGED'}
                </span>
                <span style={{ fontWeight: 700, color: '#111827' }}>{c.instrument_type}</span>
                <span style={{ color: '#374151' }}>
                  must capture <strong style={{ fontFamily: 'monospace' }}>{c.field}</strong>
                </span>
                {entry?.message && <span style={{ color: '#9ca3af' }}>“{entry.message}”</span>}
              </div>
            );
          })}
        </div>
      )}
      {otherChanges.map((c, i) => (
        <div key={i} style={{ padding: '6px 12px', fontSize: 12, color: '#374151' }}>
          ~ {c.kind === 'equipment_matrix_changed' ? 'Equipment design-data requirements changed' : 'Interlock configuration changed'}
        </div>
      ))}
    </div>
  );
};

const FindingLine: React.FC<{ f: ReviewPolicyFinding; removed?: boolean }> = ({ f, removed }) => (
  <div style={{ display: 'flex', gap: 7, alignItems: 'baseline', fontSize: 12, padding: '3px 0', textDecoration: removed ? 'line-through' : 'none', opacity: removed ? 0.6 : 1 }}>
    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: SEV_COLORS[f.severity]?.dot ?? '#9ca3af', position: 'relative', top: 1 }} />
    <span style={{ fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{f.target_ref}</span>
    <span style={{ color: '#4b5563' }}>{f.message}</span>
  </div>
);

const SOURCE_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  manual: { label: 'edited by hand', bg: '#eff6ff', text: '#1e40af' },
  agent: { label: 'via agent', bg: '#f5f3ff', text: '#6d28d9' },
  restore: { label: 'restored version', bg: '#f9fafb', text: '#6b7280' },
};

const EventTimeline: React.FC<{ events: PolicyDraftEvent[]; currentUser?: string }> = ({ events, currentUser }) => {
  const [open, setOpen] = useState<number | null>(events.length - 1);
  if (!events.length) return null;
  return (
    <div>
      {events.slice().reverse().map((e, ri) => {
        const i = events.length - 1 - ri;
        const badge = SOURCE_BADGE[e.source] ?? SOURCE_BADGE.manual;
        const who = e.by === currentUser ? 'you' : e.by;
        return (
          <div key={i} style={{ borderTop: ri ? '1px solid #f3f4f6' : 'none' }}>
            <button
              type="button"
              onClick={() => setOpen(open === i ? null : i)}
              style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', border: 'none', backgroundColor: 'transparent', padding: '7px 10px', cursor: 'pointer', textAlign: 'left', fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{timeAgo(e.at)}</span>
              <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>by <strong>{who}</strong></span>
              <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: badge.bg, color: badge.text, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>{badge.label}</span>
              <span style={{ flex: 1, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.summary ?? `${e.changes.length} change(s)`}
              </span>
              <span style={{ color: '#9ca3af' }}>{open === i ? '▾' : '▸'}</span>
            </button>
            {open === i && (
              <div style={{ padding: '0 10px 8px' }}>
                <PolicyDiff changes={e.changes} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PolicyAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<ReviewPolicyOverview | null>(null);
  const [working, setWorking] = useState<ReviewPolicyDoc | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [tab, setTab] = useState<Tab>('Policy Rules');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverChanges, setServerChanges] = useState<PolicyChange[]>([]);
  const [draftEvents, setDraftEvents] = useState<PolicyDraftEvent[]>([]);
  const [search, setSearch] = useState('');
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  const [ruleCounts, setRuleCounts] = useState<PolicyRuleCounts | null>(null);

  // agent state
  const [chat, setChat] = useState<PolicyAgentChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [proposal, setProposal] = useState<PolicyAgentChatResponse | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // impact state — the rule-count and impact endpoints scope to one domain
  const [domainOptions, setDomainOptions] = useState<{ id: string; label: string }[]>([]);
  const [domainId, setDomainId] = useState('');
  const [includeAi, setIncludeAi] = useState(false);
  const [impact, setImpact] = useState<PolicyImpactReport | null>(null);
  const [expandedImpactDoc, setExpandedImpactDoc] = useState<string | null>(null);

  // compare state
  const [compareBase, setCompareBase] = useState('published');
  const [compareWith, setCompareWith] = useState('draft');
  const [compareResult, setCompareResult] = useState<{ changes: PolicyChange[]; base_policy: ReviewPolicyDoc; compare_policy: ReviewPolicyDoc } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await policyAdminApi.getOverview();
      setOverview(data);
      setWorking(clonePolicy(data.draft ?? data.published));
      setServerChanges(data.changes ?? []);
      setDraftEvents(data.draft_events ?? []);
      setDirty(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load policy');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    domainsApi
      .list()
      .then(({ accounts }) => {
        const options = accounts.flatMap((account) =>
          account.domains.map((domain) => ({
            id: domain.id,
            label: `${account.account_name ?? account.account_id} — ${domain.name}`,
          })),
        );
        setDomainOptions(options);
        setDomainId((current) => current || options[0]?.id || '');
      })
      .catch(() => setDomainOptions([]));
  }, []);

  useEffect(() => {
    if (!domainId) return;
    policyAdminApi.getRuleCounts(domainId).then(setRuleCounts).catch(() => setRuleCounts(null));
  }, [domainId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat, proposal]);

  const mutate = (fn: (p: ReviewPolicyDoc) => void) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const next = clonePolicy(prev);
      fn(next);
      return next;
    });
    setDirty(true);
  };

  const saveDraft = useCallback(async (summary?: string, source: 'manual' | 'agent' = 'manual') => {
    if (!working) return;
    setSaveState('saving');
    setError(null);
    try {
      const res = await policyAdminApi.saveDraft(working, summary, source);
      setServerChanges(res.changes);
      setDraftEvents(res.events ?? []);
      setDirty(false);
      setSaveState('saved');
      const data = await policyAdminApi.getOverview();
      setOverview(data);
    } catch (err: any) {
      setSaveState('error');
      setError(err?.response?.data?.detail ?? 'Failed to save draft');
    }
  }, [working]);

  // Auto-save: edits persist by themselves; SMEs never manage a "draft" manually.
  const autosaveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!dirty || !working) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      void saveDraft();
    }, 1200);
    return () => {
      if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    };
  }, [dirty, working, saveDraft]);

  const publish = async () => {
    if (dirty) await saveDraft();
    if (!window.confirm(`Publish these ${serverChanges.length} change(s)? They will apply to ALL P&IDs immediately.`)) return;
    setBusy('publish');
    setError(null);
    try {
      await policyAdminApi.publish();
      await load();
      setImpact(null);
      setCompareResult(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to publish');
    } finally {
      setBusy(null);
    }
  };

  const discardDraft = async () => {
    if (!window.confirm('Throw away all draft changes and go back to the published policy?')) return;
    setBusy('discard');
    try {
      await policyAdminApi.discardDraft();
      await load();
    } finally {
      setBusy(null);
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || busy) return;
    const nextChat: PolicyAgentChatMessage[] = [...chat, { role: 'user', content: text }];
    setChat(nextChat);
    setChatInput('');
    setBusy('chat');
    setError(null);
    try {
      const res = await policyAdminApi.agentChat(nextChat);
      setChat([...nextChat, { role: 'assistant', content: res.reply }]);
      setProposal(res.proposed_policy ? res : null);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Agent unavailable');
      setChat(nextChat);
    } finally {
      setBusy(null);
    }
  };

  const applyProposal = async () => {
    if (!proposal?.proposed_policy) return;
    const proposed = clonePolicy(proposal.proposed_policy);
    setWorking(proposed);
    setProposal(null);
    setSaveState('saving');
    try {
      const res = await policyAdminApi.saveDraft(proposed, proposal.summary, 'agent');
      setServerChanges(res.changes);
      setDraftEvents(res.events ?? []);
      setDirty(false);
      setSaveState('saved');
      const data = await policyAdminApi.getOverview();
      setOverview(data);
    } catch (err: any) {
      setSaveState('error');
      setError(err?.response?.data?.detail ?? 'Failed to save agent proposal to draft');
    }
  };

  const runImpact = async () => {
    setBusy('impact');
    setError(null);
    try {
      if (dirty) await saveDraft();
      const res = await policyAdminApi.getImpact(domainId, { includeAi });
      setImpact(res);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to compute impact — make a change first so there is a draft to compare.');
    } finally {
      setBusy(null);
    }
  };

  const runCompare = async () => {
    setBusy('compare');
    setError(null);
    try {
      const res = await policyAdminApi.getDiff(compareBase, compareWith);
      setCompareResult({ changes: res.changes, base_policy: res.base_policy, compare_policy: res.compare_policy });
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to compare versions');
    } finally {
      setBusy(null);
    }
  };

  const ruleGroups = useMemo(() => {
    if (!working) return [] as Array<[string, string[]]>;
    const q = search.trim().toLowerCase();
    const groups = new Map<string, string[]>();
    for (const ruleId of Object.keys(working.rules).sort()) {
      const cfg = working.rules[ruleId];
      if (q) {
        const haystack = `${ruleId} ${cfg.title} ${cfg.description ?? ''} ${cfg.fires_when ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) continue;
      }
      const prefix = ruleId.split('-')[0];
      groups.set(prefix, [...(groups.get(prefix) ?? []), ruleId]);
    }
    return Array.from(groups.entries());
  }, [working, search]);

  if (!working) {
    return (
      <div style={{ padding: 40, fontFamily: 'system-ui', color: '#6b7280' }}>
        {error ?? 'Loading policy…'}
      </div>
    );
  }

  const publishedVersion = overview?.published.meta.db_version ?? 0;
  const versionOptions = [
    { value: 'published', label: `Published (v${publishedVersion})` },
    ...(overview?.draft ? [{ value: 'draft', label: 'Draft (unpublished)' }] : []),
    ...(overview?.versions ?? [])
      .filter((v) => v.status === 'archived')
      .map((v) => ({ value: String(v.version_no), label: `v${v.version_no} (archived)` })),
  ];
  const saveLabel =
    saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'All changes saved ✓' : saveState === 'error' ? 'Save failed — retrying on next edit' : '';

  return (
    <div style={{ fontFamily: 'system-ui', backgroundColor: '#f8fafc', minHeight: '100%' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 20px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
        <button type="button" style={btnStyle} onClick={() => navigate('/')}>← P&ID Tool</button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>Review Policy</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            Live version: <strong>v{publishedVersion}</strong>
            {overview?.draft ? ' · you are editing an unpublished draft' : ' · no pending changes'}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: saveState === 'error' ? '#b91c1c' : '#059669' }}>
          {saveLabel}
        </span>
        <button type="button" style={btnStyle} disabled={busy !== null || !overview?.draft} onClick={() => void discardDraft()}>
          Discard changes
        </button>
        <button
          type="button"
          style={{ ...primaryBtnStyle, opacity: overview?.can_publish ? 1 : 0.5 }}
          disabled={busy !== null || !overview?.can_publish || (!overview?.draft && !dirty)}
          title={overview?.can_publish ? 'Publish the draft — applies to all P&IDs' : 'Only admins / SME publishers can publish'}
          onClick={() => void publish()}>
          {busy === 'publish' ? 'Publishing…' : `Publish as v${publishedVersion + 1}`}
        </button>
      </div>

      {error && (
        <div style={{ margin: '10px 20px', padding: 10, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Draft change history — who changed what, when */}
      {draftEvents.length > 0 && (
        <div style={{ margin: '10px 20px', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', backgroundColor: '#fffbeb', borderRadius: '8px 8px 0 0' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>
              UNPUBLISHED CHANGES — the live policy (v{publishedVersion}) does NOT have these yet
            </span>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              style={{ ...btnStyle, padding: '3px 9px', fontSize: 11 }}
              onClick={() => { setTab('Versions & Compare'); setCompareBase('published'); setCompareWith('draft'); void runCompare(); }}>
              View full diff
            </button>
          </div>
          <EventTimeline events={draftEvents} currentUser={undefined} />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 20px 0' }}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              ...btnStyle,
              backgroundColor: tab === t ? '#111827' : 'white',
              color: tab === t ? 'white' : '#334155',
              borderRadius: '8px 8px 0 0',
            }}>
            {t}
          </button>
        ))}
      </div>

      <div style={{ margin: '0 20px 30px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0 8px 8px 8px', padding: 14 }}>
        {tab === 'Policy Rules' && (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search rules… (e.g. 'equipment', 'set pressure', 'interlock')"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 10px', width: 340 }}
              />
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#6b7280' }}>
                {(Object.keys(SEV_LABEL) as ReviewPolicySeverity[]).map((sev) => (
                  <span key={sev} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: SEV_COLORS[sev].dot }} />
                    <strong style={{ color: SEV_COLORS[sev].text }}>{sev}</strong> = {SEV_LABEL[sev]}
                  </span>
                ))}
              </div>
            </div>

            {ruleGroups.map(([prefix, ruleIds]) => (
              <div key={prefix} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: '#111827', margin: '4px 0 6px' }}>
                  {GROUP_NAMES[prefix] ?? prefix}
                  <span style={{ color: '#9ca3af', fontWeight: 600 }}> · {ruleIds.length} rules</span>
                </div>
                {ruleIds.map((ruleId) => {
                  const cfg = working.rules[ruleId];
                  const expanded = expandedRule === ruleId;
                  const count = ruleCounts?.rule_counts[ruleId] ?? 0;
                  const docCount = ruleCounts?.rule_docs[ruleId] ?? 0;
                  return (
                    <div
                      key={ruleId}
                      style={{
                        border: `1px solid ${expanded ? '#c7d2fe' : '#e5e7eb'}`,
                        borderRadius: 10,
                        marginBottom: 6,
                        backgroundColor: cfg.enabled ? 'white' : '#fafafa',
                        opacity: cfg.enabled ? 1 : 0.65,
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} title={cfg.enabled ? 'Active — click to switch off' : 'Off — click to activate'}>
                          <span
                            onClick={() => mutate((p) => { p.rules[ruleId].enabled = !cfg.enabled; })}
                            style={{ width: 34, height: 19, borderRadius: 999, backgroundColor: cfg.enabled ? '#059669' : '#d1d5db', position: 'relative', flexShrink: 0 }}>
                            <span style={{ position: 'absolute', top: 2, left: cfg.enabled ? 17 : 2, width: 15, height: 15, borderRadius: '50%', backgroundColor: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
                          </span>
                        </label>
                        <div
                          style={{ flex: 1, cursor: 'pointer', minWidth: 0 }}
                          onClick={() => setExpandedRule(expanded ? null : ruleId)}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                            {cfg.title}
                            <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af', marginLeft: 8 }}>{ruleId}</span>
                            {cfg.signoff_only && (
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 999, padding: '1px 6px', marginLeft: 6 }}>
                                only at sign-off
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap' }}>
                            {cfg.description ?? ''}
                          </div>
                        </div>
                        {ruleCounts && (
                          <span
                            title={`Currently fires ${count} time(s) across ${docCount} of ${ruleCounts.documents_evaluated} P&IDs (${domainOptions.find((option) => option.id === ruleCounts.domain_id)?.label ?? ruleCounts.domain_id})`}
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: count ? '#374151' : '#c0c4cc',
                              backgroundColor: count ? '#f3f4f6' : 'transparent',
                              border: '1px solid #e5e7eb',
                              borderRadius: 999,
                              padding: '2px 8px',
                              whiteSpace: 'nowrap',
                            }}>
                            {count ? `${count}× on ${docCount} sheets` : 'not firing'}
                          </span>
                        )}
                        <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                          {(Object.keys(SEV_LABEL) as ReviewPolicySeverity[]).map((sev) => {
                            const c = SEV_COLORS[sev];
                            const active = cfg.severity === sev;
                            return (
                              <button
                                key={sev}
                                type="button"
                                disabled={!cfg.enabled}
                                onClick={() => mutate((p) => { p.rules[ruleId].severity = sev; })}
                                title={`${sev} — ${SEV_LABEL[sev]}`}
                                style={{
                                  border: 'none',
                                  padding: '5px 10px',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: cfg.enabled ? 'pointer' : 'not-allowed',
                                  backgroundColor: active ? c.dot : 'white',
                                  color: active ? 'white' : '#9ca3af',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  lineHeight: 1.3,
                                  minWidth: 86,
                                }}>
                                <span>{sev}</span>
                                <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.9 }}>{SEV_LABEL[sev]}</span>
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedRule(expanded ? null : ruleId)}
                          style={{ ...btnStyle, padding: '4px 8px', fontSize: 11 }}>
                          {expanded ? 'Hide' : 'Details'}
                        </button>
                      </div>
                      {expanded && (
                        <div style={{ borderTop: '1px solid #eef2f7', padding: '10px 14px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', letterSpacing: 0.5, marginBottom: 3 }}>WHEN DOES THIS FIRE?</div>
                            <div style={{ fontSize: 12, color: '#374151' }}>{cfg.fires_when ?? '—'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', letterSpacing: 0.5, marginBottom: 3 }}>EXAMPLE</div>
                            <div style={{ fontSize: 12, color: '#374151', fontStyle: 'italic' }}>{cfg.example ?? '—'}</div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', letterSpacing: 0.5, marginBottom: 3 }}>RULE NAME (editable)</div>
                            <input
                              type="text"
                              value={cfg.title}
                              onChange={(e) => mutate((p) => { p.rules[ruleId].title = e.target.value; })}
                              style={{ width: '100%', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px', color: '#374151' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {ruleGroups.length === 0 && (
              <div style={{ padding: 20, fontSize: 12, color: '#9ca3af' }}>No rules match "{search}".</div>
            )}
          </div>
        )}

        {tab === 'Instrument Requirements' && (
          <div>
            <div style={{ padding: 10, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', marginBottom: 12 }}>
              <strong>This is part of the same policy</strong> — each row says “instruments of this type must have this field filled in”.
              Every row is wired to a rule from the Policy Rules tab (shown with its severity); when the field is left empty on a P&ID, that rule fires.
            </div>
            {Object.entries(working.instrument_required_fields).sort().map(([itype, entries]) => (
              <div key={itype} style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10 }}>
                <div style={{ padding: '6px 10px', backgroundColor: '#f9fafb', fontWeight: 800, fontSize: 12, color: '#111827', borderRadius: '8px 8px 0 0' }}>{itype}</div>
                {entries.map((entry) => {
                  const ruleCfg = working.rules[entry.rule];
                  return (
                    <div key={entry.field} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 10px', borderTop: '1px solid #f3f4f6', fontSize: 12 }}>
                      <span style={{ color: '#374151' }}>must capture</span>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, width: 170 }}>{entry.field}</span>
                      <SevChip sev={ruleCfg?.severity} />
                      <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af' }}>{entry.rule}</span>
                      <input
                        type="text"
                        value={entry.message ?? ''}
                        title="The message annotators see when this fires"
                        onChange={(e) => mutate((p) => {
                          const target = p.instrument_required_fields[itype].find((x) => x.field === entry.field);
                          if (target) target.message = e.target.value;
                        })}
                        style={{ flex: 1, fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 6px', color: '#374151' }}
                      />
                      <button
                        type="button"
                        style={{ ...btnStyle, color: '#b91c1c', padding: '3px 8px' }}
                        onClick={() => mutate((p) => {
                          p.instrument_required_fields[itype] = p.instrument_required_fields[itype].filter((x) => x.field !== entry.field);
                          if (!p.instrument_required_fields[itype].length) delete p.instrument_required_fields[itype];
                        })}>
                        remove
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
            <AddRequiredFieldForm
              onAdd={(itype, field, rule, message) => mutate((p) => {
                if (!p.rules[rule]) p.rules[rule] = { severity: 'FLAG', enabled: true, title: `${field} captured` };
                const entries = (p.instrument_required_fields[itype] ?? []).filter((x) => x.field !== field);
                entries.push({ rule, field, message: message || `${itype} without ${field}` });
                p.instrument_required_fields[itype] = entries;
              })}
            />
          </div>
        )}

        {tab === 'Agent' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, height: 540 }}>
            {/* Left: chat */}
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                {chat.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9ca3af', padding: 12 }}>
                    Describe the policy change in plain language — e.g. “Safety Valve set pressure is safety critical, make it a blocker”.
                    The agent proposes changes; you see them on the right before anything is applied.
                  </div>
                )}
                {chat.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                    <div style={{ maxWidth: '85%', fontSize: 12, padding: '7px 10px', borderRadius: 10, whiteSpace: 'pre-wrap', backgroundColor: m.role === 'user' ? '#2563eb' : '#f3f4f6', color: m.role === 'user' ? 'white' : '#111827' }}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {busy === 'chat' && <div style={{ fontSize: 12, color: '#9ca3af', padding: 6 }}>Agent thinking…</div>}
                <div ref={chatEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 8, padding: 8, borderTop: '1px solid #e5e7eb' }}>
                <input
                  type="text"
                  value={chatInput}
                  placeholder="Ask the policy agent…"
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void sendChat(); }}
                  style={{ flex: 1, fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px' }}
                />
                <button type="button" style={primaryBtnStyle} disabled={busy !== null || !chatInput.trim()} onClick={() => void sendChat()}>
                  Send
                </button>
              </div>
            </div>
            {/* Right: live policy diff — proposal first, else current draft-vs-published */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflowY: 'auto' }}>
              {proposal?.changes ? (
                <div>
                  <div style={{ position: 'sticky', top: 0, padding: '8px 12px', backgroundColor: '#eff6ff', borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#1e40af' }}>AGENT PROPOSAL — not applied yet</span>
                    <div style={{ flex: 1 }} />
                    <button type="button" style={primaryBtnStyle} disabled={busy !== null} onClick={() => void applyProposal()}>Apply</button>
                    <button type="button" style={btnStyle} onClick={() => setProposal(null)}>Reject</button>
                  </div>
                  <div style={{ padding: 10 }}>
                    <PolicyDiff changes={proposal.changes} afterPolicy={proposal.proposed_policy} beforePolicy={working} />
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ position: 'sticky', top: 0, padding: '8px 12px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, fontWeight: 800, color: '#374151' }}>
                    YOUR DRAFT vs LIVE POLICY (v{publishedVersion}) — updates as you edit
                  </div>
                  <div style={{ padding: 10 }}>
                    {serverChanges.length ? (
                      <PolicyDiff changes={serverChanges} afterPolicy={working} beforePolicy={overview?.published} />
                    ) : (
                      <div style={{ padding: 12, fontSize: 12, color: '#9ca3af' }}>
                        No pending changes — your draft matches the live policy. Ask the agent or edit a rule; the difference appears here.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'Fleet Impact' && (
          <div>
            <div style={{ padding: 10, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', marginBottom: 12 }}>
              <strong>What is this?</strong> Before you publish, this runs BOTH the live policy and your draft on every P&ID of the domain and
              shows exactly which sheets would get new issues (or lose old ones) because of your changes.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>
                Domain{' '}
                <select value={domainId} onChange={(e) => setDomainId(e.target.value)} style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', maxWidth: 260 }}>
                  {domainOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>
                <input type="checkbox" checked={includeAi} onChange={(e) => setIncludeAi(e.target.checked)} /> include drawing-evidence rules (slower)
              </label>
              <button type="button" style={primaryBtnStyle} disabled={busy !== null || !domainId} onClick={() => void runImpact()}>
                {busy === 'impact' ? 'Checking every P&ID…' : 'What would my changes do?'}
              </button>
            </div>
            {impact && (() => {
              const changedDocs = impact.docs.filter((r) => r.delta.total !== 0 || r.delta.blockers !== 0 || r.delta.flags !== 0);
              const dB = impact.totals.compare.blockers - impact.totals.base.blockers;
              const dF = impact.totals.compare.flags - impact.totals.base.flags;
              return (
                <div>
                  <div style={{ padding: 10, borderRadius: 8, backgroundColor: '#fffbeb', border: '1px solid #fde68a', fontSize: 13, color: '#111827', marginBottom: 10 }}>
                    If you publish now: <strong style={{ color: '#b91c1c' }}>{dB >= 0 ? `+${dB}` : dB} blocking issues</strong> and{' '}
                    <strong style={{ color: '#b45309' }}>{dF >= 0 ? `+${dF}` : dF} review flags</strong> across{' '}
                    <strong>{changedDocs.length} of {impact.docs.length} P&IDs</strong>.
                    {changedDocs.length === 0 && ' Your changes have no effect on current data.'}
                  </div>
                  {changedDocs.map((row) => {
                    const expanded = expandedImpactDoc === row.doc_id;
                    return (
                      <div key={row.doc_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
                        <button
                          type="button"
                          onClick={() => setExpandedImpactDoc(expanded ? null : row.doc_id)}
                          style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', border: 'none', backgroundColor: '#fffdf5', padding: '8px 12px', cursor: 'pointer', textAlign: 'left', fontSize: 12 }}>
                          <span style={{ fontWeight: 700, color: '#111827', width: 220 }}>{row.pnid_no ?? row.doc_id}</span>
                          <span style={{ color: row.delta.blockers > 0 ? '#b91c1c' : '#6b7280' }}>
                            blocking: {row.base.blockers} → {row.compare.blockers}
                          </span>
                          <span style={{ color: row.delta.flags > 0 ? '#b45309' : '#6b7280' }}>
                            flags: {row.base.flags} → {row.compare.flags}
                          </span>
                          <span style={{ flex: 1 }} />
                          <span style={{ color: '#2563eb', fontWeight: 700, fontSize: 11 }}>{expanded ? 'hide' : 'what changes?'}</span>
                        </button>
                        {expanded && (
                          <div style={{ padding: '8px 14px', borderTop: '1px solid #f3f4f6' }}>
                            {(row.new_examples ?? []).length > 0 && (
                              <div style={{ marginBottom: 6 }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: '#047857', letterSpacing: 0.5, marginBottom: 3 }}>NEW ISSUES ANNOTATORS WILL SEE</div>
                                {(row.new_examples ?? []).map((f, i) => <FindingLine key={i} f={f} />)}
                              </div>
                            )}
                            {(row.removed_examples ?? []).length > 0 && (
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 800, color: '#b91c1c', letterSpacing: 0.5, marginBottom: 3 }}>ISSUES THAT GO AWAY</div>
                                {(row.removed_examples ?? []).map((f, i) => <FindingLine key={i} f={f} removed />)}
                              </div>
                            )}
                            {!(row.new_examples ?? []).length && !(row.removed_examples ?? []).length && (
                              <div style={{ fontSize: 12, color: '#6b7280' }}>Same findings, different severity: {Object.entries(row.rule_deltas).map(([r, d]) => `${r} ${d > 0 ? '+' : ''}${d}`).join(', ') || 'severity re-classification only'}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {tab === 'Versions & Compare' && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: '#374151' }}>Compare</span>
              <select value={compareBase} onChange={(e) => setCompareBase(e.target.value)} style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px' }}>
                {versionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ color: '#9ca3af' }}>with</span>
              <select value={compareWith} onChange={(e) => setCompareWith(e.target.value)} style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px' }}>
                {versionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button type="button" style={primaryBtnStyle} disabled={busy !== null} onClick={() => void runCompare()}>
                {busy === 'compare' ? 'Comparing…' : 'Show differences'}
              </button>
            </div>
            {compareResult && (
              <div style={{ marginBottom: 16 }}>
                <PolicyDiff changes={compareResult.changes} afterPolicy={compareResult.compare_policy} beforePolicy={compareResult.base_policy} />
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '10px 0 4px' }}>VERSION HISTORY</div>
            {(overview?.versions ?? []).map((v) => (
              <div key={v.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '7px 8px', borderTop: '1px solid #f3f4f6', fontSize: 12 }}>
                <span style={{ fontWeight: 800, width: 40 }}>v{v.version_no}</span>
                <span style={{ width: 130, fontWeight: 700, color: v.status === 'published' ? '#047857' : v.status === 'draft' ? '#b45309' : '#6b7280' }}>
                  {v.status === 'published' ? 'LIVE now' : v.status === 'draft' ? 'draft (unpublished)' : 'previous version'}
                </span>
                <span style={{ flex: 1, color: '#374151' }}>{v.change_summary ?? '—'}</span>
                <span style={{ color: '#9ca3af', fontSize: 11 }}>
                  {v.published_by
                    ? `published by ${v.published_by} · ${timeAgo(v.published_at)}`
                    : `last edited by ${v.updated_by ?? v.created_by} · ${timeAgo(v.updated_at ?? v.created_at)}`}
                </span>
                {v.status === 'archived' && (
                  <button
                    type="button"
                    style={btnStyle}
                    onClick={async () => {
                      await policyAdminApi.restoreVersion(v.version_no);
                      await load();
                    }}>
                    Restore to draft
                  </button>
                )}
              </div>
            ))}
            {(overview?.versions ?? []).length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: '#9ca3af' }}>
                No versions yet — the built-in seed policy (v0) is live. Making an edit creates a draft; publishing creates v1.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AddRequiredFieldForm: React.FC<{
  onAdd: (itype: string, field: string, rule: string, message: string) => void;
}> = ({ onAdd }) => {
  const [itype, setItype] = useState('');
  const [field, setField] = useState('');
  const [message, setMessage] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 10, border: '1px dashed #d1d5db', borderRadius: 8 }}>
      <input type="text" placeholder="Instrument type (e.g. Safety Valve)" value={itype} onChange={(e) => setItype(e.target.value)} style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', width: 180 }} />
      <input type="text" placeholder="field to require (e.g. orifice_designation)" value={field} onChange={(e) => setField(e.target.value)} style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', width: 240 }} />
      <input type="text" placeholder="message annotators see (optional)" value={message} onChange={(e) => setMessage(e.target.value)} style={{ flex: 1, fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px' }} />
      <button
        type="button"
        style={btnStyle}
        disabled={!itype.trim() || !field.trim()}
        onClick={() => {
          const f = field.trim();
          onAdd(itype.trim(), f, `INST-M-${f.toUpperCase().slice(0, 12)}`, message.trim());
          setItype(''); setField(''); setMessage('');
        }}>
        + Add requirement
      </button>
    </div>
  );
};

export default PolicyAdminPage;
